import { useCallback, useEffect, useRef, useState } from "react"
import { apiClient } from "@/lib/api/client"

/** State machine of the recorder + transcribe round-trip:
 *  - ``idle``         — ready, no mic permission acquired.
 *  - ``recording``    — mic open, ``MediaRecorder`` running, ``stream``
 *                       handed out for the LiveWaveform.
 *  - ``transcribing`` — recording stopped, audio uploaded to
 *                       ``/api/issues/transcribe/``, awaiting text.
 *  - ``error``        — last attempt failed (mic permission denied,
 *                       network error, etc.). The next ``start()``
 *                       resets back to recording.
 */
export type AudioRecorderStatus = "idle" | "recording" | "transcribing" | "error"

export interface UseAudioRecorderOptions {
  /** Called with the final transcript when the API responds. The
   *  composer appends it to the current text. */
  onTranscription: (text: string) => void
  /** Optional: surface a user-facing toast when something fails. */
  onError?: (error: Error) => void
}

export interface UseAudioRecorderResult {
  status: AudioRecorderStatus
  /** True iff the browser supports MediaRecorder + getUserMedia. */
  isSupported: boolean
  /** The current mic stream while ``status === "recording"``. Pass
   *  this to ``<LiveWaveform stream={...}/>`` so the visualizer
   *  shares the same mic — opening a second one would prompt for
   *  permission twice and double the device's "in use" indicator. */
  stream: MediaStream | null
  start: () => Promise<void>
  /** Stop the recorder and upload the audio for transcription. */
  stop: () => void
  /** Bail out of the current turn. If the recorder is still running
   *  it stops and the chunks are dropped; if the upload is already
   *  in flight, it is aborted *and* the response is ignored should
   *  it land before the abort propagates. */
  cancel: () => void
}

const RECORDER_MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
] as const

function pickRecorderMime(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined
  for (const mime of RECORDER_MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(mime)) return mime
  }
  return undefined
}

/** Per-turn state captured at ``start()`` and consulted by both the
 *  ``onstop`` handler and ``cancel()``. Holding it in a ref (rather
 *  than spread across boolean refs) makes the cancel-after-stop
 *  race obvious: ``cancel()`` flips ``cancelled`` and aborts
 *  ``controller``; the in-flight upload's success branch checks
 *  ``cancelled`` before forwarding the transcript. */
interface TurnToken {
  cancelled: boolean
  controller: AbortController | null
}

/** Hook for the chat composer's voice-input flow.
 *
 *  Flow:
 *    1. ``start()`` — request mic, open ``MediaRecorder``, expose
 *       ``stream`` for the visualizer, set status=recording.
 *    2. ``stop()`` — stop the recorder; on ``onstop`` we upload the
 *       blob to ``/api/issues/transcribe/`` and resolve via
 *       ``onTranscription``. Status flips ``recording → transcribing →
 *       idle``.
 *    3. ``cancel()`` — abandon the current turn at any stage. Aborts
 *       the upload if it is mid-flight and ignores the response if
 *       it already returned.
 *
 *  We deliberately *don't* mix MediaRecorder + Web Speech API. The
 *  whole point of the new flow is server-side transcription: it works
 *  in every browser (Firefox doesn't have SpeechRecognition) and we
 *  already pay for OpenAI for the rest of the chat.
 */
export function useAudioRecorder({
  onTranscription,
  onError,
}: UseAudioRecorderOptions): UseAudioRecorderResult {
  const [status, setStatus] = useState<AudioRecorderStatus>("idle")
  const [stream, setStream] = useState<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const tokenRef = useRef<TurnToken>({ cancelled: false, controller: null })
  const mimeRef = useRef<string | undefined>(undefined)
  const onTranscriptionRef = useRef(onTranscription)
  const onErrorRef = useRef(onError)

  useEffect(() => {
    onTranscriptionRef.current = onTranscription
  }, [onTranscription])
  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  const isSupported =
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined"

  const cleanupStream = useCallback(() => {
    setStream((s) => {
      s?.getTracks().forEach((t) => t.stop())
      return null
    })
  }, [])

  const start = useCallback(async () => {
    if (!isSupported) {
      onErrorRef.current?.(new Error("Voice input is not available in this browser."))
      return
    }
    if (recorderRef.current && recorderRef.current.state !== "inactive") return

    // Fresh token for this turn — captured into the ``onstop`` closure
    // below so a later ``cancel()`` flips the *same* token's flags.
    const token: TurnToken = { cancelled: false, controller: null }
    tokenRef.current = token
    chunksRef.current = []

    let micStream: MediaStream
    try {
      micStream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Microphone access was denied.")
      setStatus("error")
      onErrorRef.current?.(error)
      return
    }

    const mime = pickRecorderMime()
    mimeRef.current = mime
    let recorder: MediaRecorder
    try {
      recorder = mime ? new MediaRecorder(micStream, { mimeType: mime }) : new MediaRecorder(micStream)
    } catch (err) {
      micStream.getTracks().forEach((t) => t.stop())
      const error = err instanceof Error ? err : new Error("Couldn't start the recorder.")
      setStatus("error")
      onErrorRef.current?.(error)
      return
    }

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data)
    }

    recorder.onstop = async () => {
      // Always release the mic — keep the indicator off even if the
      // upload fails or the user cancelled.
      cleanupStream()
      if (token.cancelled) {
        chunksRef.current = []
        setStatus("idle")
        return
      }
      const chunks = chunksRef.current
      chunksRef.current = []
      if (chunks.length === 0) {
        setStatus("idle")
        onErrorRef.current?.(new Error("Nothing was recorded."))
        return
      }

      const blob = new Blob(chunks, { type: mimeRef.current ?? chunks[0].type ?? "audio/webm" })
      setStatus("transcribing")

      const controller = new AbortController()
      token.controller = controller
      try {
        const formData = new FormData()
        const ext = guessExtension(blob.type)
        formData.append("audio", blob, `voice.${ext}`)
        const { data } = await apiClient.post<{ text: string }>(
          "/api/issues/transcribe/",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            signal: controller.signal,
          },
        )
        // The user could have hit Cancel between the request resolving
        // and this line running — re-check the token before applying.
        if (token.cancelled) {
          setStatus("idle")
          return
        }
        const text = (data?.text ?? "").trim()
        if (text) onTranscriptionRef.current(text)
        setStatus("idle")
      } catch (err) {
        if (token.cancelled || isAbortError(err)) {
          // Cancel path already settled the UI to idle.
          if (!token.cancelled) setStatus("idle")
          return
        }
        setStatus("error")
        const error = err instanceof Error ? err : new Error("Transcription failed.")
        onErrorRef.current?.(error)
      }
    }

    recorderRef.current = recorder
    setStream(micStream)
    setStatus("recording")
    recorder.start()
  }, [cleanupStream, isSupported])

  const stop = useCallback(() => {
    const recorder = recorderRef.current
    if (!recorder || recorder.state === "inactive") return
    try {
      recorder.stop()
    } catch {
      // ignore — onstop will run even on a forced stop in most browsers
    }
  }, [])

  const cancel = useCallback(() => {
    const recorder = recorderRef.current
    const token = tokenRef.current
    token.cancelled = true
    // Abort any in-flight upload so we don't pay for a transcript
    // the user explicitly bailed out of.
    token.controller?.abort()
    if (recorder && recorder.state !== "inactive") {
      try {
        recorder.stop()
      } catch {
        // ignore
      }
    } else {
      // Recorder already inactive — clean up the visible UI directly.
      cleanupStream()
      setStatus("idle")
    }
  }, [cleanupStream])

  // Tear down on unmount (e.g. user closes the chat panel mid-recording).
  useEffect(() => {
    return () => {
      const token = tokenRef.current
      token.cancelled = true
      token.controller?.abort()
      const recorder = recorderRef.current
      if (recorder && recorder.state !== "inactive") {
        try {
          recorder.stop()
        } catch {
          // ignore
        }
      }
      cleanupStream()
    }
  }, [cleanupStream])

  return { status, isSupported, stream, start, stop, cancel }
}

function guessExtension(mime: string): string {
  if (mime.startsWith("audio/webm")) return "webm"
  if (mime.startsWith("audio/mp4")) return "m4a"
  if (mime.startsWith("audio/ogg")) return "ogg"
  if (mime.startsWith("audio/wav") || mime.startsWith("audio/x-wav")) return "wav"
  return "webm"
}

function isAbortError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false
  const anyErr = err as { name?: string; code?: string }
  return anyErr.name === "AbortError" || anyErr.code === "ERR_CANCELED"
}
